import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbInputModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import { PreInscricao } from '../../../core/api/pre-inscricao.dto';
import { PreInscricaoService } from '../../../core/api/pre-inscricao.service';
import { mensagemDeErro } from '../../../utils/mensagem-erro';

/**
 * Campanha de pré-inscrição: criação e edição.
 *
 * `dataReferenciaIdade` é o dia em que a idade do inscrito é apurada — não é o dia do envio.
 * É esse campo que faz "10 anos" significar a mesma coisa para quem se inscreve em março e
 * para quem se inscreve em novembro.
 */
@Component({
  selector: 'app-pre-inscricao-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbInputModule,
    NbCheckboxModule,
    NbSpinnerModule,
  ],
  template: `
    <nb-card class="dialog-card pre-inscricao-form">
      <nb-card-header>{{ campanha?.id ? 'Editar campanha' : 'Nova campanha' }}</nb-card-header>

      <nb-card-body>
        <label class="field-label" for="pi-nome">Nome da campanha</label>
        <input
          nbInput
          fullWidth
          id="pi-nome"
          name="nome"
          placeholder="Ex.: Catequese 2027"
          [(ngModel)]="nome"
          [class.ng-invalid]="tentouSalvar && !nome.trim()"
          [class.ng-touched]="tentouSalvar"
        />

        <label class="field-label" for="pi-data">Idade apurada em</label>
        <input
          nbInput
          fullWidth
          id="pi-data"
          name="dataReferenciaIdade"
          type="date"
          [(ngModel)]="dataReferenciaIdade"
          [class.ng-invalid]="tentouSalvar && !dataReferenciaIdade"
          [class.ng-touched]="tentouSalvar"
        />
        <p class="ajuda">
          A idade de quem se inscreve é calculada nesta data, e é ela que define o grupo.
        </p>

        <nb-checkbox [(ngModel)]="ativa" name="ativa">
          Campanha no ar (o link público aceita envios)
        </nb-checkbox>
      </nb-card-body>

      <nb-card-footer class="footer-actions">
        <button nbButton status="basic" size="medium" type="button" (click)="fechar()">Cancelar</button>
        <button
          nbButton
          status="primary"
          size="medium"
          type="button"
          [disabled]="salvando"
          [nbSpinner]="salvando"
          (click)="salvar()"
        >
          Salvar
        </button>
      </nb-card-footer>
    </nb-card>
  `,
  styles: [
    `
      .pre-inscricao-form .field-label {
        display: block;
        margin: 0.9rem 0 0.35rem;
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        color: var(--text-hint-color, #8f9bb3);
      }

      .pre-inscricao-form .field-label:first-child {
        margin-top: 0;
      }

      .ajuda {
        margin: 0.35rem 0 1rem;
        font-size: 0.82rem;
        color: var(--text-hint-color, #8f9bb3);
      }
    `,
  ],
})
export class PreInscricaoFormComponent implements OnInit {
  @Input() campanha?: PreInscricao;

  nome = '';
  dataReferenciaIdade = '';
  ativa = true;
  salvando = false;
  tentouSalvar = false;

  constructor(
    private ref: NbDialogRef<PreInscricaoFormComponent>,
    private service: PreInscricaoService,
    private toastr: NbToastrService,
  ) {}

  ngOnInit(): void {
    if (!this.campanha) return;
    this.nome = this.campanha.nome ?? '';
    this.dataReferenciaIdade = this.campanha.dataReferenciaIdade ?? '';
    this.ativa = this.campanha.ativa !== false;
  }

  salvar(): void {
    this.tentouSalvar = true;
    const nome = this.nome.trim();
    if (!nome || !this.dataReferenciaIdade) {
      this.toastr.warning('Nome e data de apuração da idade são obrigatórios.', 'Campos obrigatórios');
      return;
    }

    const corpo = { nome, dataReferenciaIdade: this.dataReferenciaIdade, ativa: this.ativa };
    const id = this.campanha?.id;
    this.salvando = true;

    const req = id ? this.service.atualizar(id, { ...corpo, id }) : this.service.criar(corpo);
    req.pipe(finalize(() => (this.salvando = false))).subscribe({
      next: () => {
        this.toastr.success(id ? 'Campanha atualizada.' : `${nome} criada.`, 'Pré-inscrição');
        this.ref.close(true);
      },
      error: (err) =>
        this.toastr.danger(mensagemDeErro(err, 'Não foi possível salvar a campanha.'), 'Erro'),
    });
  }

  fechar(): void {
    this.ref.close(false);
  }
}
